import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Spinner from '../common/Spinner';
import Button from '../common/Button';
import Toast from '../common/Toast';
import PaymentApproval from './PaymentApproval';

const MashellDashboard = () => {
  const [activeTrip, setActiveTrip] = useState(null);   // { trip_id, plate_number }
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);

  // Check for an existing active trip on mount
  const fetchActiveTrip = useCallback(async () => {
    try {
      const { data } = await api.get('/mashell/active-trip');
      if (data) {
        setActiveTrip({ trip_id: data.id, plate_number: data.plate_number });
      } else {
        setActiveTrip(null);
      }
    } catch (err) {
      setToast({ message: 'Could not check active trip', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveTrip();
  }, [fetchActiveTrip]);

  // Fetch the list of all vehicles for the dropdown
  const fetchVehicles = async () => {
    try {
      const { data } = await api.get('/mashell/vehicles');
      setVehicles(data);
    } catch (err) {
      setToast({ message: 'Could not load vehicles', type: 'error' });
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  // Start a new boarding
  const handleStart = async () => {
    if (!selectedVehicle) {
      setToast({ message: 'Select a vehicle', type: 'error' });
      return;
    }
    try {
      const { data } = await api.post('/mashell/start-trip', { vehicle_id: selectedVehicle });
      const vehicle = vehicles.find(v => v.id == selectedVehicle);
      setActiveTrip({ trip_id: data.trip_id, plate_number: vehicle?.plate_number });
      setSelectedVehicle('');
      setSearchTerm('');
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Could not start boarding', type: 'error' });
    }
  };

  // End the current trip
  const handleEndTrip = async () => {
    if (!window.confirm('End this trip? Passengers will no longer see it.')) return;
    try {
      await api.post('/mashell/end-trip');
      setActiveTrip(null);
      setToast({ message: 'Trip ended', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to end trip', type: 'error' });
    }
  };

  if (loading) return <Spinner />;

  // ---------- ACTIVE TRIP VIEW ----------
  if (activeTrip) {
    return (
      <div className="fade-in">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <Button variant="outline" onClick={handleEndTrip}>
            End Trip
          </Button>
          <span style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--primary)' }}>
            🚌 {activeTrip.plate_number}
          </span>
        </div>
        <PaymentApproval tripId={activeTrip.trip_id} plateNumber={activeTrip.plate_number} />
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  // ---------- START NEW BOARDING VIEW ----------
  const filteredVehicles = vehicles.filter(v =>
    v.plate_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="glass-card fade-in">
      <h2>Start New Boarding</h2>
      <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
        Search for a vehicle and start collecting payments.
      </p>

      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="🔍 Search by plate number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            border: '2px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '1rem',
            marginBottom: '0.5rem'
          }}
        />
      </div>

      <select
        size={Math.min(filteredVehicles.length, 8)}
        value={selectedVehicle}
        onChange={(e) => setSelectedVehicle(e.target.value)}
        style={{
          width: '100%',
          padding: '0.5rem',
          border: '2px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '1rem',
          marginBottom: '1rem'
        }}
      >
        {filteredVehicles.map(v => (
          <option key={v.id} value={v.id}>
            {v.plate_number} – {v.model} {v.route_description ? `(${v.route_description})` : ''}
          </option>
        ))}
        {filteredVehicles.length === 0 && (
          <option disabled>No vehicles found</option>
        )}
      </select>

      <Button onClick={handleStart} disabled={!selectedVehicle}>
        Start Boarding
      </Button>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default MashellDashboard;